import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import theme from './styles/theme';
import { Amplify } from 'aws-amplify';
import { amplifyConfig } from '@/config/cognito.config';
import ProtectedRoute from '@/components/ProtectedRoute';
import Header from '@/components/Header';
import UserLogin from '@/components/UserLogin';
import QuickSightDashboard from '@/components/QuickSightDashboard';
import UserAccessPage from '@/components/UserAccess';
import UserActivityDashboard from '@/components/UserActivityDashboard';
import { UserProvider } from '@/context/UserContext';
import { DashboardProvider } from '@/context/DashboardContext';
import { AuthService } from '@/services/auth.service';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ROUTES } from '@/config/constant';
import './styles/App.css';

// Configure Amplify
Amplify.configure(amplifyConfig);

export const App: React.FC = () => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkUserAuthentication();
  }, []);

  /**
   * Verifies if a valid, unexpired Cognito session exists in local storage
   */
  const checkUserAuthentication = async () => {
    try {
      const authenticated = await AuthService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (!authenticated) {
        console.log('No active session found. Rendering in-app UserLogin screen.');
      } else {
        console.log('Session verified successfully. Loading analytical framework.');
      }
    } catch (error) {
      console.error('Error verifying background auth state:', error);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  /**
   * Callback executed by the UserLogin component upon successful validation
   */
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  if (isCheckingAuth) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem'
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Intercept unauthenticated traffic right on the target Amplify URL
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <UserLogin onLoginSuccess={handleLoginSuccess} />
      </ThemeProvider>
    );
  }

  // Render Core Application Interface upon valid state resolution
  return (
    <ThemeProvider theme={theme}>
      <UserProvider>
        <DashboardProvider>
          <ProtectedRoute>
            <AppContent />
          </ProtectedRoute>
        </DashboardProvider>
      </UserProvider>
    </ThemeProvider>
  );
};

/**
 * App content component that conditionally renders the header based on current route
 */
const AppContent: React.FC = () => {
  const location = useLocation();
  const isUserAccessPage = location.pathname === ROUTES.USER_ACCESS;
  const isUserActivityPage = location.pathname === ROUTES.USER_ACTIVITY;
  const hideHeader = isUserAccessPage || isUserActivityPage;

  return (
    <div className="app">
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: 0,
        padding: 0,
        background: '#f4f6fa'
      }}>
        {/* Conditionally render header - hide on user access / user activity pages */}
        {!hideHeader && (
          <div style={{ flex: '0 0 auto', zIndex: 2 }}>
            <Header />
          </div>
        )}
        <div style={{ flex: '1 1 auto', minHeight: 0, zIndex: 1 }}>
          <Routes>
            {/* Static asset routes - do not render QuickSight component */}
            <Route path="/assets/*" element={null} />
            
            {/* User Access Management Page */}
            <Route path={ROUTES.USER_ACCESS} element={<UserAccessPage />} />

            {/* User Activity Dashboard (CloudWatch analytics) - admin only */}
            <Route path={ROUTES.USER_ACTIVITY} element={<UserActivityDashboard />} />
            
            {/* Dashboard routes */}
            <Route 
              path="/:dashboardId?" 
              element={
                <QuickSightDashboard
                  height="100%"
                  className="quicksight-dashboard-full"
                />
              } 
            />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default App;